import { useEffect, useState } from 'react';

import { transport } from '@/api/transport';
import { ApiError, type ApiErrorCode, type BridgePreview } from '@/api/types';

export type PreviewState =
  | { readonly status: 'idle' }
  | { readonly status: 'ready'; readonly preview: BridgePreview }
  | { readonly status: 'error'; readonly error: ApiErrorCode };

/**
 * What a link shows before anyone opens it: the kind of bridge and of its items. Asking spends no view and brings no
 * ciphertext, so it is safe to do on load. 'idle' is until the answer comes. A view is spent only by the person's own click (`useBridge`'s `ready`).
 */
export function usePreview(bridgeId: string, enabled: boolean): PreviewState {
  const [state, setState] = useState<PreviewState>({ status: 'idle' });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    transport
      .preview(bridgeId)
      .then((preview) => {
        if (!cancelled) setState({ status: 'ready', preview });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', error: error instanceof ApiError ? error.code : 'internal' });
      });
    return () => {
      cancelled = true;
    };
  }, [bridgeId, enabled]);

  return state;
}
